// benchmark-products-api-only-v1

const API_BASE = (
  process.env.GATSBY_API_BASE ||
  "https://api.benchmark.ae/v1"
).replace(/\/$/, "")

const WORKSPACE_KEY = (
  process.env.GATSBY_WORKSPACE_KEY ||
  "benchmark-digital-solutions"
).trim()

const CACHE_KEY = `benchmark-api-products:${WORKSPACE_KEY}`
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000

export const productsApiHost = API_BASE.replace(/\/v1$/, "")

export function absoluteProductAsset(value) {
  if (!value) return ""
  if (/^https?:\/\//i.test(value)) return value
  return `${productsApiHost}${value.startsWith("/") ? "" : "/"}${value}`
}

export function cachedProducts() {
  if (typeof window === "undefined") return []

  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > CACHE_MAX_AGE) return []

    return Array.isArray(parsed.products) ? parsed.products : []
  } catch (_) {
    return []
  }
}

export async function fetchProducts({ timeoutMs = 4500 } = {}) {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

  try {
    const response = await fetch(`${API_BASE}/public/website/products`, {
      headers: {
        Accept: "application/json",
        "X-Workspace-Key": WORKSPACE_KEY,
      },
      signal: controller ? controller.signal : undefined,
      cache: "default",
    })

    if (!response.ok) throw new Error(`Products API HTTP ${response.status}`)

    const body = await response.json()
    const products = Array.isArray(body?.products) ? body.products : []

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ savedAt: Date.now(), products }),
        )
      } catch (_) {}
    }

    return products
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export function productText(product, lang, field) {
  const locale = lang === "ar" ? "ar" : "en"
  return (
    product?.[`${field}_${locale}`] ||
    product?.[`${field}_${locale === "ar" ? "en" : "ar"}`] ||
    ""
  )
}

export function productDetailTitle(product, lang) {
  const locale = lang === "ar" ? "ar" : "en"
  const values = product?.metadata?.detail_titles || {}

  return (
    values[locale] ||
    values[locale === "ar" ? "en" : "ar"] ||
    productText(product, lang, "title")
  )
}

export function legacyProductPath(product, lang) {
  const locale = lang === "ar" ? "ar" : "en"
  const paths = product?.metadata?.legacy_paths || {}

  return String(
    paths[locale] ||
    paths[locale === "ar" ? "en" : "ar"] ||
    "",
  ).replace(/^\/+/, "")
}

export function publicProductPath(product, lang) {
  const legacy = legacyProductPath(product, lang)

  if (
    legacy &&
    (legacy.startsWith("products/") || legacy.startsWith("ar/products/"))
  ) {
    return legacy
  }

  return `${lang === "ar" ? "ar/" : ""}products/${product.slug}`
}

export function productImage(product) {
  return absoluteProductAsset(
    product?.banner_image_url ||
    (Array.isArray(product?.image_urls) ? product.image_urls[0] : ""),
  )
}

export function productMatchesPath(product, pathname, lang) {
  const current = String(pathname || "").replace(/^\/+|\/+$/g, "")
  const legacy = legacyProductPath(product, lang).replace(/^\/+|\/+$/g, "")

  if (legacy && current === legacy) return true

  const prefix = lang === "ar" ? "ar/products/" : "products/"
  return current === `${prefix}${product.slug}`
}
